import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Card, Row, Col, Typography, Tag, Button, Space, Spin, message, Alert,
} from 'antd';
import { MedicineBoxOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { drugAPI } from '../services/api';

const { Text } = Typography;

interface DrugItem {
  drug_id: string;
  drug_name: string;
  drug_name_en?: string;
  category?: string;
  status?: string;
  activated: boolean;
}

interface DrugActivationProps {
  visible: boolean;
  onClose: () => void;
  onActivated: (drugIds: string[]) => void;
}

const DrugActivation: React.FC<DrugActivationProps> = ({ visible, onClose, onActivated }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [drugs, setDrugs] = useState<DrugItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [initialActivatedIds, setInitialActivatedIds] = useState<string[]>([]);
  const [multiDrug, setMultiDrug] = useState(false);
  const [maxDrugs, setMaxDrugs] = useState(0);
  const [activationScope, setActivationScope] = useState<'tenant' | 'user'>('tenant');
  const [canManageActivation, setCanManageActivation] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    drugAPI.activationStatus()
      .then((res) => {
        const activated = res.activated_ids || [];
        setDrugs(res.all_drugs || []);
        setMultiDrug(Boolean(res.multi_drug));
        setMaxDrugs(res.max_drugs || 0);
        setActivationScope(res.activation_scope === 'user' ? 'user' : 'tenant');
        setCanManageActivation(Boolean(res.can_manage_activation));
        setSelectedIds(activated);
        setInitialActivatedIds(activated);
      })
      .catch(() => message.error('加载药物列表失败'))
      .finally(() => setLoading(false));
  }, [visible]);

  const effectiveMax = multiDrug ? maxDrugs : 1;
  const quotaFull = effectiveMax > 0 && selectedIds.length >= effectiveMax;
  const readonlyUserAssignment = activationScope === 'user' || !canManageActivation;

  const hasChanges = useMemo(() => (
    selectedIds.length !== initialActivatedIds.length
    || selectedIds.some((id) => !initialActivatedIds.includes(id))
  ), [initialActivatedIds, selectedIds]);

  const toggleDrug = (drugId: string) => {
    if (readonlyUserAssignment) return;
    setSelectedIds((prev) => {
      if (prev.includes(drugId)) {
        return prev.filter((id) => id !== drugId);
      }

      if (!multiDrug) {
        return [drugId];
      }

      if (maxDrugs > 0 && prev.length >= maxDrugs) {
        message.warning(`当前套餐最多可激活 ${maxDrugs} 种药物`);
        return prev;
      }

      return [...prev, drugId];
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await drugAPI.activate(selectedIds);
      message.success(res.message);
      onActivated(selectedIds);
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : '保存激活药物失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={(
        <Space>
          <MedicineBoxOutlined />
          <span>药物激活</span>
        </Space>
      )}
      open={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>取消</Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          disabled={!hasChanges || readonlyUserAssignment}
          onClick={handleSubmit}
        >
          确认保存 ({selectedIds.length}{effectiveMax > 0 ? `/${effectiveMax}` : ''})
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <>
          {readonlyUserAssignment && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="系统管理员已为当前账号指定可用药物，此处仅展示，不可自行修改。"
            />
          )}
          <Alert
            type={selectedIds.length === 0 ? 'warning' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              multiDrug
                ? maxDrugs > 0
                  ? `当前套餐支持多药物管理，最多可激活 ${maxDrugs} 种药物；已选药物可取消，也可在额度内重新调整。`
                  : '当前套餐支持多药物管理；已选药物可取消，也可继续调整激活列表。'
                : '当前套餐仅支持激活 1 种药物；你可以取消当前药物，或直接改选另一种药物。'
            }
          />
          <Row gutter={[12, 12]}>
            {drugs.map((drug) => {
              const selected = selectedIds.includes(drug.drug_id);
              const initiallyActivated = initialActivatedIds.includes(drug.drug_id);
              const clickable = !readonlyUserAssignment && !(!selected && quotaFull && multiDrug);

              return (
                <Col xs={24} sm={12} key={drug.drug_id}>
                  <Card
                    hoverable={clickable}
                    size="small"
                    onClick={() => clickable && toggleDrug(drug.drug_id)}
                    style={{
                      borderColor: selected ? '#0B2545' : undefined,
                      background: selected ? '#e6f7ff' : (!clickable ? '#f5f5f5' : undefined),
                      cursor: !clickable ? 'not-allowed' : 'pointer',
                      opacity: !clickable ? 0.5 : 1,
                    }}
                  >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space>
                        {selected && <CheckCircleOutlined style={{ color: '#0B2545' }} />}
                        <Text strong>{drug.drug_name}</Text>
                        {drug.drug_name_en ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            ({drug.drug_name_en})
                          </Text>
                        ) : null}
                      </Space>
                      <Space size={4} wrap>
                        {drug.category ? <Tag>{drug.category}</Tag> : null}
                        {drug.status === 'inactive' ? <Tag color="gold">研究开放</Tag> : null}
                        {initiallyActivated ? <Tag color="green">当前已激活</Tag> : null}
                        {selected && !initiallyActivated ? <Tag color="blue">本次新增</Tag> : null}
                        {!selected && initiallyActivated ? <Tag color="orange">本次将取消</Tag> : null}
                      </Space>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
          {drugs.length === 0 && (
            <Text type="secondary">暂无可用药物配置</Text>
          )}
        </>
      )}
    </Modal>
  );
};

export default DrugActivation;
